<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <div class="d-flex justify-content-between align-items-start mb-3 affected-results__header">
        <page-heading>Affected Results</page-heading>
        <div class="affected-results__actions">
          <app-button variant="outline-primary" class="btn-block-mob" :block="false" @click="goBack">Back</app-button>
          <app-button
            v-if="!previewed"
            class="btn-block-mob ml-lg-2"
            :block="false"
            :loading="previewing"
            :disabled="!results.length"
            @click="handlePreview"
          >Recalculate</app-button>
          <app-button
            v-else
            class="btn-block-mob ml-lg-2"
            :block="false"
            :loading="recalculating"
            @click="handleConfirm"
          >Confirm Recalculate</app-button>
        </div>
      </div>

      <div v-if="state" class="affected-results__state mb-3">
        <span><span class="font-weight-bolder">Effective Date: </span>{{ formatDate(state.effective_date, 'long') }}</span>
        <span><span class="font-weight-bolder">BOY Status: </span>{{ state.boy_status.name }}</span>
        <span><span class="font-weight-bolder">{{ type === 'horse' ? 'Horse Status' : 'Rider Status' }}: </span>{{ type === 'horse' ? state.horse_status.name : state.rider_status.name }}</span>
        <span><span class="font-weight-bolder">Changed By: </span>{{ state.changed_by.name }} on {{ formatDate(state.changed_by.date, 'long') }}</span>
        <span v-if="state.needs_recalculation" class="text-warning">
          Needs recalculation<span class="draws-dashboard__warn-icon" title="Needs recalculation" />
        </span>
      </div>

      <div class="alert alert-danger mb-3" v-if="error">{{ error }}</div>

      <div v-if="previewed" :class="['alert mb-3', hasChanges ? 'alert-warning' : 'alert-success']">
        <template v-if="hasChanges">
          Recalculation preview succeeded — points changed on {{ changedCount }} result{{ changedCount === 1 ? '' : 's' }}: {{ affectedResultsCount }} for this {{ type === 'horse' ? 'horse' : 'rider' }} (see below)<span v-if="otherResults.length">, and {{ otherResults.length }} other affected result{{ otherResults.length === 1 ? '' : 's' }} shown further down</span>.
          This isn't entirely normal — it may be worth double-checking these manually before confirming. To save these changes, click <strong>"Confirm Recalculate"</strong> above.
        </template>
        <template v-else>
          Recalculation preview succeeded — no results were affected. Please double-check that no points have decreased, then click <strong>"Confirm Recalculate"</strong> above to save.
        </template>
        <div class="mt-2">Before confirming, make sure the event is affiliated — if it isn't, its points will be recalculated to zero.</div>
      </div>

      <affected-results-table
        :rows="results"
        :loading="loading || previewing"
        :previewed="previewed"
        :entity-label="type === 'horse' ? 'Rider' : 'Horse'"
        :entity-field="type === 'horse' ? 'rider' : 'horse'"
      />

      <template v-if="previewed && otherResults.length">
        <h2 class="h4 mt-4">Other Affected Results</h2>
        <p class="text-muted">Other riders/horses whose points are also affected by this recalculation.</p>
        <affected-results-table
          :rows="otherResults"
          :loading="loading || previewing"
          :previewed="previewed"
          mode="both"
        />
      </template>
    </div>

    <action-gate-modal v-if="gateVisible" :error="gateError" :loading="gateLoading" @confirm="submitGatePassword" @cancel="cancelGate" />
  </tab-layout>
</template>

<script>
import {
  defineComponent, ref, computed, onMounted, getCurrentInstance
} from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import ActionGateModal from '@/components/ActionGateModal'
import AffectedResultsTable from '@/components/BoyStatus/AffectedResultsTable'
import useAffectedResults from '@/composables/useAffectedResults'
import useActionGate from '@/composables/useActionGate'
import { formatDate } from '@/utils/date'

function extractError(err) {
  const data = err.response != null ? err.response.data : null
  return data != null && data.message != null
    ? data.message
    : 'A server error occurred. Please try again later.'
}

export default defineComponent({
  name: 'AffectedResultsView',
  components: {
    TabLayout, PageHeading, AppButton, ActionGateModal, AffectedResultsTable
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: value => ['rider', 'horse'].includes(value)
    },
    entityId: { type: [String, Number], required: true },
    stateId: { type: [String, Number], required: true }
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    const {
      state, results, otherResults, loading, previewing, previewed, recalculating, fetchAffected, preview, confirmRecalculate
    } = useAffectedResults(props.type)
    const actionGate = useActionGate()
    const error = ref(null)

    const affectedResultsCount = computed(() => (
      results.value.filter(row => String(row.points) !== String(row.points_after)).length
    ))
    const hasChanges = computed(() => affectedResultsCount.value > 0 || otherResults.value.length > 0)
    const changedCount = computed(() => affectedResultsCount.value + otherResults.value.length)

    function goBack() {
      const profileName = props.type === 'horse' ? 'horses' : 'members'
      proxy.$router.push(`/${profileName}/${props.entityId}#boy-history`)
    }

    onMounted(() => {
      proxy.$nextTick(() => {
        proxy.$root.$emit('update-breadcrumb', {
          name: props.type === 'horse' ? 'Horse Profile' : 'Member Profile',
          link: `${props.type === 'horse' ? 'horses' : 'members'}/${props.entityId}#boy-history`
        })
      })
      actionGate.runGated(() => fetchAffected(props.entityId, props.stateId))
        .catch((err) => { if (!err.isGateCancelled) error.value = extractError(err) })
    })

    async function handlePreview() {
      error.value = null
      try {
        await actionGate.runGated(() => preview(props.entityId, props.stateId))
      } catch (err) {
        if (!err.isGateCancelled) error.value = extractError(err)
      }
    }

    async function handleConfirm() {
      error.value = null
      try {
        await actionGate.runGated(() => confirmRecalculate(props.entityId, props.stateId))
        goBack()
      } catch (err) {
        if (!err.isGateCancelled) error.value = extractError(err)
      }
    }

    return {
      state,
      results,
      otherResults,
      loading,
      previewing,
      previewed,
      recalculating,
      error,
      hasChanges,
      affectedResultsCount,
      changedCount,
      formatDate,
      goBack,
      handlePreview,
      handleConfirm,
      ...actionGate
    }
  }
})
</script>

<style scoped>
@media (max-width: 500px) {
  .affected-results__header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
}

.affected-results__state {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}

.draws-dashboard__warn-icon {
  display: inline-block;
  background-image: url('~@/assets/img/sprite.png');
  background-size: 99px 97px;
  background-position: -22px 0px;
  width: 21px;
  height: 18px;
  vertical-align: middle;
  margin-left: 0.4rem;
}
</style>



// WEBPACK FOOTER //
// src/views/BoyStatus/AffectedResults/AffectedResultsView.vue